import { Mail } from 'lucide-react'
import { useState } from 'react'
import { EmailDetailModal } from '@/components/emails/email-detail-modal'
import { EmailFilters, type EmailFilterValues } from '@/components/emails/email-filters'
import { EmailTable } from '@/components/emails/email-table'
import { useEmail, useEmails } from '@/hooks/use-emails'
import type { EmailRecord } from '@/lib/types'

const DEFAULT_PER_PAGE = 25

const INITIAL_FILTERS: EmailFilterValues = {
  status: '',
  account: '',
  domain: '',
  from: '',
  to: '',
  search: '',
}

export function EmailExplorerPage(): React.JSX.Element {
  const [filters, setFilters] = useState<EmailFilterValues>(INITIAL_FILTERS)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null)

  const emailsQuery = useEmails({
    page,
    per_page: perPage,
    status: filters.status || undefined,
    search: filters.search || undefined,
    account: filters.account || undefined,
    domain: filters.domain || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
  })

  const emailDetailQuery = useEmail(selectedEmailId)

  function handleApplyFilters(newFilters: EmailFilterValues): void {
    setFilters(newFilters)
    setPage(1)
  }

  function handleRowClick(email: EmailRecord): void {
    setSelectedEmailId(email.id)
  }

  function handleCloseDetail(): void {
    setSelectedEmailId(null)
  }

  function handlePerPageChange(newPerPage: number): void {
    setPerPage(newPerPage)
    setPage(1)
  }

  const pagination = emailsQuery.data
    ? {
        page: emailsQuery.data.page,
        perPage: emailsQuery.data.per_page,
        total: emailsQuery.data.total,
      }
    : null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Mail className="h-6 w-6 text-admin-500" />
        <h1 className="text-2xl font-semibold text-text-primary">Email Explorer</h1>
      </div>

      {/* Filter bar */}
      <EmailFilters values={filters} onApply={handleApplyFilters} />

      {/* Results table */}
      <EmailTable
        data={emailsQuery.data?.data ?? []}
        isLoading={emailsQuery.isLoading}
        onRowClick={handleRowClick}
        pagination={pagination}
        onPageChange={setPage}
        onPerPageChange={handlePerPageChange}
      />

      {/* Detail modal */}
      <EmailDetailModal
        email={emailDetailQuery.data}
        isLoading={emailDetailQuery.isLoading}
        open={selectedEmailId !== null}
        onClose={handleCloseDetail}
      />
    </div>
  )
}
